/**
 * /for-developers — dev-focused funnel landing. The technical surfaces
 * (CLI / MCP / slash commands / implementation_status) live here in
 * depth, freeing the main homepage to keep the PM story clean.
 *
 * Designed for HN / dev-Twitter / Reddit r/ClaudeAI traffic. Heavier on
 * code blocks + technical specifics than ForPm.
 */
import { useTranslation } from 'react-i18next';
import { MarketingShell } from './MarketingShell';

export function ForDevelopers() {
  const { t } = useTranslation();

  return (
    <MarketingShell
      activeRoute="/for-developers"
      currentLocale={typeof window !== 'undefined' && window.location.pathname.startsWith('/zh/') ? 'zh' : 'en'}
      hreflangPath="/for-developers"
      documentTitle={t('forDevelopers.pageTitle')}
      documentDescription={t('forDevelopers.pageDescription')}
    >
      {/* ── Hero ── */}
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 88 }}>
        <div style={{ maxWidth: 880 }}>
          <span className="label label-amber">{t('forDevelopers.heroLabel')}</span>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(44px, 5.6vw, 76px)',
              lineHeight: 1,
              margin: '12px 0 24px',
            }}
          >
            {t('forDevelopers.heroTitle')}
            <br />
            <em>{t('forDevelopers.heroTitleEm')}</em>
          </h1>
          <p className="lede" style={{ maxWidth: 720 }}>{t('forDevelopers.heroLede')}</p>
          <div className="actions" style={{ marginTop: 28 }}>
            <a className="btn-primary" href="/">{t('forDevelopers.ctaButton')}</a>
            <a className="btn-ghost" href="https://github.com/anthropics/claude-code" target="_blank" rel="noreferrer">
              Claude Code →
            </a>
          </div>
        </div>
      </section>

      {/* ── 4 surface deep-dive ── */}
      <section className="surfaces">
        <div className="head">
          <span className="label label-amber">FIG. 1 · INTEGRATION SURFACES</span>
          <h2 className="serif">
            Four ways your cards <em>land in your tools.</em>
          </h2>
        </div>
        <div className="surfaces-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="surface">
            <div className="sh">
              <span className="stitle">A · CLI</span>
              <span className="stag">draftlize pull</span>
            </div>
            <div className="body" style={{ padding: 24 }}>
              <h4 className="serif" style={{ fontSize: 18, margin: '0 0 10px' }}>{t('forDevelopers.surface1Title')}</h4>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--ink-1)', margin: 0 }}>{t('forDevelopers.surface1Body')}</p>
            </div>
          </div>
          <div className="surface">
            <div className="sh">
              <span className="stitle">B · MCP SERVER</span>
              <span className="stag">stdio JSON-RPC</span>
            </div>
            <div className="body" style={{ padding: 24 }}>
              <h4 className="serif" style={{ fontSize: 18, margin: '0 0 10px' }}>{t('forDevelopers.surface2Title')}</h4>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--ink-1)', margin: 0 }}>{t('forDevelopers.surface2Body')}</p>
            </div>
          </div>
          <div className="surface">
            <div className="sh">
              <span className="stitle">C · SLASH COMMANDS</span>
              <span className="stag">/draftlize:cite</span>
            </div>
            <div className="body" style={{ padding: 24 }}>
              <h4 className="serif" style={{ fontSize: 18, margin: '0 0 10px' }}>{t('forDevelopers.surface3Title')}</h4>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--ink-1)', margin: 0 }}>{t('forDevelopers.surface3Body')}</p>
            </div>
          </div>
          <div className="surface">
            <div className="sh">
              <span className="stitle">D · STATUS TRACKING</span>
              <span className="stag">mark-built</span>
            </div>
            <div className="body" style={{ padding: 24 }}>
              <h4 className="serif" style={{ fontSize: 18, margin: '0 0 10px' }}>{t('forDevelopers.surface4Title')}</h4>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--ink-1)', margin: 0 }}>{t('forDevelopers.surface4Body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="pricing" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div className="head">
          <h2 className="serif">
            {t('forDevelopers.ctaTitle')} <em>{t('forDevelopers.ctaTitleEm')}</em>
          </h2>
        </div>
        <div className="trust-strip">
          <span>{t('homepage.pricingTrust1')}</span>
          <span>{t('homepage.pricingTrust2')}</span>
          <span>{t('homepage.pricingTrust3')}</span>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '40px 24px',
            border: '1px solid var(--ink-0)',
            borderTop: 0,
            background: 'var(--paper-1)',
          }}
        >
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            {t('forDevelopers.ctaBody')}
          </p>
          <a className="btn-primary" href="/login">{t('forDevelopers.ctaButton')}</a>
        </div>
      </section>
    </MarketingShell>
  );
}
